/**
 * Veterinarian Detail Component
 * - Displays veterinarian profile and credentials
 * - Shows specialization, experience, and contact details
 * - Includes KYC status management and document downloads
 */

import React, { useState } from "react";
import {
  FaRegEnvelope,
  FaStethoscope,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaCheckCircle,
  FaClinicMedical,
  FaFileInvoice,
  FaUserMd,
} from "react-icons/fa";
import {
  MdPhoneInTalk,
  MdOutlinePets,
  MdOutlineAssignment,
} from "react-icons/md";
import { IoMdTime } from "react-icons/io";
import AvatarBlock from "./Shared/AvatarBlock";
import InfoRow from "./Shared/InfoRow";
import SectionHeader from "./Shared/SectionHeader";
import KycStatusModal from "../common/KycStatusModal";
import ImageModal from "../common/ImageModal";
import { updateUserAPI } from "../../services/userAPI";
import { toast } from "react-toastify";

const VeterinarianDetail = ({ data, onUpdate }) => {
  const [showKycModal, setShowKycModal] = useState(false);
  const [selectedKycStatus, setSelectedKycStatus] = useState("");
  const [localData, setLocalData] = useState(data);
  const [selectedImage, setSelectedImage] = useState(null);

  const image = localData.profile_image_url || "https://placehold.co/80x80";
  const name = localData.name || "NA";
  const email = localData.email || "NA";
  const mobile =
    localData.country_code && localData.mobile
      ? `${localData.country_code} ${localData.mobile}`
      : localData.mobile || "NA";
  const userCode = localData.user_code || "NA";
  const city = localData.city_name || "NA";
  const experience = localData.vendor?.years_of_experience || "NA";
  const clinicName = localData.vendor?.clinic_name || "NA";
  const specialization = localData.vendor?.specialization || "NA";
  const gstin = localData.vendor?.gstin || "NA";
  const biography = localData.vendor?.biography || "NA";
  const kycStatus = localData.vendor?.kyc_status || "NA";
  const consultationCount = localData.consultation_count || 0;
  const isActive = localData.active;
  const profileCompleted = localData.profile_completed;

  // ✅ Dynamically prepare document list with links
  const documents = [];
  if (localData.vendor?.license_document) {
    documents.push({
      label: "License",
      url: localData.vendor.license_document,
    });
  }
  if (localData.vendor?.certification_document) {
    documents.push({
      label: "Certificate",
      url: localData.vendor.certification_document,
    });
  }
  if (localData.vendor?.special_certifications) {
    documents.push({
      label: "Special Certificate",
      url: localData.vendor.special_certifications,
    });
  }

  const basicInfoItems = [
    { icon: FaRegEnvelope, label: email },
    { icon: MdPhoneInTalk, label: mobile },
    { icon: FaMapMarkerAlt, label: city },
    { icon: MdOutlineAssignment, label: `User Code: ${userCode}` },
  ];

  const professionalInfoItems = [
    { icon: FaClinicMedical, label: clinicName },
    {
      icon: FaStethoscope,
      label: specialization !== "NA" ? specialization : "General Practice",
    },
    {
      icon: IoMdTime,
      label:
        experience !== "NA"
          ? `${experience} years experience`
          : "Experience: NA",
    },
    {
      icon: FaFileInvoice,
      label: gstin !== "NA" ? `GST: ${gstin}` : "GST: NA",
    },
    {
      icon: FaUserMd,
      label: biography !== "NA" ? `Biography: ${biography}` : "Biography: NA",
    },
  ];

  // Handle KYC status change
  const handleKycStatusChange = (newStatus) => {
    if (newStatus !== kycStatus) {
      setSelectedKycStatus(newStatus);
      setShowKycModal(true);
    }
  };

  // Confirm KYC status update
  const confirmKycStatusUpdate = async () => {
    try {
      // Send minimal FormData - just the kyc_status field
      const formData = new FormData();
      formData.append('kyc_status', selectedKycStatus);

      await updateUserAPI("veterinarians", data.uuid, formData);

      // Update local data immediately for UI update
      const updatedData = {
        ...localData,
        vendor: {
          ...localData.vendor,
          kyc_status: selectedKycStatus
        }
      };
      setLocalData(updatedData);

      // Call onUpdate callback if provided
      if (onUpdate) {
        onUpdate(updatedData);
      }

      toast.success('KYC status updated successfully!');
      setShowKycModal(false);
    } catch (error) {
      console.error("Error updating KYC status:", error);
      const errorMessage = error?.response?.data?.meta?.message || 'Failed to update KYC status. Please try again.';
      toast.error(errorMessage);
    }
  };

  const statsInfoItems = [
    { icon: MdOutlineAssignment, label: `${consultationCount} consultations` },
  ];

  // ✅ Function to handle document download
  const handleDownload = (url, label) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = label;
    link.target = "_blank";
    link.click();
  };

  return (
    <div className="flex flex-col gap-6 lg:gap-8 mt-6 lg:mt-10 " >
      {/* Header with Avatar and Status */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <AvatarBlock image={image} name={name} onImageClick={setSelectedImage} />
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
          {profileCompleted && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
              <FaCheckCircle className="w-4 h-4 mr-1" />
              Profile Completed
            </span>
          )}
          <span
            className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
              isActive
                ? "bg-green-100 text-green-800"
                : "bg-red-100 text-red-800"
            }`}
          >
            <FaCheckCircle className="w-4 h-4 mr-1" />
            {isActive ? "Active" : "Inactive"}
          </span>
        </div>
      </div>

      {/* Basic Information */}
      <div>
        <SectionHeader>Basic Information</SectionHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 lg:gap-6 text-gray-800 text-sm sm:text-base mt-4 pl-4 lg:ml-20">
          <div className="flex flex-col gap-3">
            {basicInfoItems.slice(0, 2).map((item, idx) => (
              <InfoRow key={idx} icon={item.icon}>
                {item.label}
              </InfoRow>
            ))}
          </div>
          <div className="flex flex-col gap-3">
            {basicInfoItems.slice(2).map((item, idx) => (
              <InfoRow key={idx} icon={item.icon}>
                {item.label}
              </InfoRow>
            ))}
          </div>
        </div>
      </div>

      {/* Professional Information */}
      <div>
        <SectionHeader>Professional Information</SectionHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 lg:gap-6 text-gray-800 text-sm sm:text-base mt-4 pl-4 lg:ml-20">
          <div className="flex flex-col gap-3">
            {professionalInfoItems.slice(0, 2).map((item, idx) => (
              <InfoRow key={idx} icon={item.icon}>
                {item.label}
              </InfoRow>
            ))}
          </div>
          <div className="flex flex-col gap-3">
            {professionalInfoItems.slice(2).map((item, idx) => (
              <InfoRow key={idx} icon={item.icon}>
                {item.label}
              </InfoRow>
            ))}
          </div>
        </div>
      </div>



      {/* Statistics */}
      <div>
        <SectionHeader>Statistics</SectionHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 lg:gap-6 text-gray-800 text-sm sm:text-base mt-4 pl-4 lg:ml-20">
          <div className="flex flex-col gap-3">
            {statsInfoItems.slice(0, 2).map((item, idx) => (
              <InfoRow key={idx} icon={item.icon} className={item.color}>
                {item.label}
              </InfoRow>
            ))}
          </div>
          <div className="flex flex-col gap-3">
            {statsInfoItems.slice(2).map((item, idx) => (
              <InfoRow key={idx} icon={item.icon} className={item.color}>
                {item.label}
              </InfoRow>
            ))}
          </div>
        </div>
      </div>

      {/* KYC Status Management */}
      <div>
        <SectionHeader>KYC Status</SectionHeader>
        <div className="mt-4 pl-4 lg:ml-20">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-gray-700">Status:</label>
              <select
                value={kycStatus}
                onChange={(e) => handleKycStatusChange(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="pending">Pending</option>
                <option value="verified">Verified</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            <div className="text-sm text-gray-600">
              Current: <span className="font-medium capitalize">{kycStatus}</span>
            </div>
          </div>
        </div>
      </div>

      {/* ✅ Documents Section */}
      <div>
        <SectionHeader>Documents</SectionHeader>
        <div className="mt-4 flex flex-wrap gap-3">
          {documents.length > 0 ? (
            documents.map((doc, i) => (
              <button
                key={i}
                onClick={() => handleDownload(doc.url, doc.label)}
                className="border border-blue-400 px-5 py-2 rounded-lg text-blue-700 font-medium bg-white hover:bg-blue-50 text-base transition-all"
              >
                {doc.label}
              </button>
            ))
          ) : (
            <div className="text-gray-500 text-sm italic">
              No documents available
            </div>
          )}
        </div>
      </div>

      {/* KYC Status Confirmation Modal */}
      <KycStatusModal
        open={showKycModal}
        onClose={() => setShowKycModal(false)}
        onConfirm={confirmKycStatusUpdate}
        currentStatus={kycStatus}
        newStatus={selectedKycStatus}
        veterinarianName={name}
      />

      {/* Image Modal */}
      <ImageModal
        open={!!selectedImage}
        onClose={() => setSelectedImage(null)}
        imageSrc={selectedImage}
        altText={`${name} profile image`}
      />
    </div>
  );
};

export default VeterinarianDetail;
